import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, Search, ShoppingCart, CreditCard, Truck, RotateCcw, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import pageCover from "@/assets/kupish/p-navodila.webp";

const TITLE = "Uporabniška navodila za kupce — kupish.si";
const DESC =
  "Kako nakupovati na kupish.si: iskanje izdelkov, košarica z izdelki več trgovin, oddaja naročila, plačilo, dostava, vračilo in stik s ponudnikom.";
const URL = "https://kupish.si/uporabniska-navodila";

const STEPS = [
  {
    icon: Search,
    title: "1. Poiščite izdelek",
    text: "Izdelke iščete prek iskalnika ali kategorij. V seznamu lahko filtrirate po kategoriji, ceni in ponudniku. Pri vsakem izdelku je jasno navedeno, katera trgovina ga prodaja.",
  },
  {
    icon: ShoppingCart,
    title: "2. Dodajte v košarico",
    text: "Košarica lahko vsebuje izdelke različnih ponudnikov. Količine uredite v košarici, kjer vidite tudi skupni znesek in razdelitev po prodajalcih.",
  },
  {
    icon: CreditCard,
    title: "3. Oddajte naročilo",
    text: "Na blagajni vnesete podatke za dostavo in oddate naročilo. Naročilo prejme vsak ponudnik posebej in vam izda račun oziroma predračun.",
  },
  {
    icon: Truck,
    title: "4. Prejem pošiljke",
    text: "Izdelki so novi in običajno na zalogi, dobavni rok je najpogosteje 1–5 delovnih dni. Če ste naročili pri več trgovinah, lahko pošiljke prispejo ločeno.",
  },
  {
    icon: RotateCcw,
    title: "5. Vračilo ali reklamacija",
    text: "Za odstop od pogodbe imate 14 dni. Reklamacijo in garancijo uveljavljate pri ponudniku, pri katerem ste izdelek kupili.",
  },
  {
    icon: MessageCircle,
    title: "6. Vprašanje ponudniku",
    text: "Na strani izdelka in na profilu trgovine je gumb za povpraševanje. Ponudniki običajno odgovorijo v enem delovnem dnevu.",
  },
];

export const Route = createFileRoute("/uporabniska-navodila")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "sl_SI" },
      { property: "og:site_name", content: "kupish.si" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Kako nakupovati na kupish.si",
          description: DESC,
          inLanguage: "sl-SI",
          url: URL,
          step: STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title.replace(/^\d+\.\s*/, ""),
            text: s.text,
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <article className="pb-20">
      <PageHero src={pageCover} alt="Uporabniška navodila kupish.si — minimalističen 3D prikaz priročnika" innerClassName="text-center">
        <div className="eyebrow justify-center inline-flex items-center gap-2">
          <BookOpen className="h-3.5 w-3.5" /> Za kupce
        </div>
        <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-tight">
          Uporabniška <span className=" text-accent">navodila</span>
        </h1>
        <p data-answer className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Na kupish.si kupujete nove izdelke različnih ponudnikov in spletnih trgovin.
          V enem naročilu lahko združite izdelke več trgovin — spodaj je opisan celoten
          postopek od iskanja do dostave in morebitnega vračila.
        </p>
      </PageHero>
      <div className="mx-auto max-w-4xl px-6 pt-4">

      <ol className="mt-14 grid sm:grid-cols-2 gap-5">
        {STEPS.map((s) => (
          <li key={s.title} className="flex gap-4 p-5 border border-border rounded-lg bg-card">
            <span className="icon-badge-sm shrink-0">
              <s.icon className="h-4 w-4" />
            </span>
            <div>
              <h2 className="font-serif text-lg leading-snug">{s.title}</h2>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </div>
          </li>
        ))}
      </ol>

      <section className="mt-16 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-serif text-2xl">Kdo je prodajalec?</h2>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          kupish.si upravlja nakupovalno središče in tehnično platformo, prodajalec pa je ponudnik, ki
          je izdelek objavil. Ponudnik izda račun, odpošlje izdelek ter odgovarja za
          garancijo, vračila in reklamacije. Njegovo ime je vedno navedeno pri izdelku.
        </p>
        <div className="mt-5 flex flex-wrap gap-4 text-sm">
          <Link to="/pogosta-vprasanja" className="text-accent underline underline-offset-4">Pogosta vprašanja</Link>
          <Link to="/reklamacije" className="text-accent underline underline-offset-4">Reševanje reklamacij</Link>
          <Link to="/zaupanje-in-varnost" className="text-accent underline underline-offset-4">Zaupanje in varnost</Link>
          <Link to="/pogoji" className="text-accent underline underline-offset-4">Splošni pogoji</Link>
        </div>
      </section>
      </div>
    </article>
  );
}
